import * as React from 'react';
import { IFeatureFlags, ITenantConfig } from 'data/tenant-config';
import { sendApiRequest } from 'utils/network';
import { classNames } from 'utils/ui/css';
import { brandNames } from 'data/schema';
import { getCurrentUrlParameter } from 'utils/url';
import { ITenant } from './types';
import { NewMarketForm } from './NewMarketForm';
import { NewTenantForm } from './NewTenantForm';
import MarketDashboard from './MarketDashboard';
import { user } from 'utils/auth/user';
import { CustomerFacingError } from 'utils/ui/errors';
import { EErrorCode } from 'config/errors-codes';
import ConfiguratorValidationDialog from 'components/ConfiguratorValidationDialog';

// Constants for configurator field names to avoid hardcoded strings
const CONFIGURATOR_FIELDS = {
  DCC: 'selectionDCC',
  SCC: 'selectionSCC',
} as const;

const columns = new Map([
  ['id', 'ID'],
  ['brand', 'Brand'],
  ['market', 'Market'],
  ['tenant', 'Tenant'],
  ['enabled', 'Enabled'],
  ['selectionVin', 'VIN'],
  ['selectionCode', 'Code'],
  ['selectionCarline', 'Carlines'],
  ['selectionDCC', 'DCC'],
  ['selectionSCC', 'SCC'],
  ['sharingDga', 'DGA'],
  ['sharingSameDevice', 'Same Device'],
  ['sharingBroadcast', 'Broadcast from Cloud'],
  ['sharingBroadcastLocal', 'Broadcast from Local'],
  ['sharingRemote', 'Remote'],
  ['voip', 'VoIP'],
  ['wltp', 'WLTP'],
  ['2D', '2D'],
  ['carDetails', 'Car Details'],
  // ['brandSwitcher', '[DEV] Brand Switcher'],
  // ['localeSwitcher', '[DEV] Locale Switcher'],
]);

const editableFields = Array.from(columns.keys()).filter(
  (key) => ['brand', 'market', 'id', 'tenant', 'enabled'].indexOf(key) === -1
);

function normalizeId(id: string) {
  return id.replace(/[^a-z0-9]/gi, '');
}

export const ConfigurationTable = () => {
  const elementRef = React.useRef<HTMLTableElement>(null);
  const [rows, setRows] = React.useState<ITenantConfig[]>([]);
  const [form, setForm] = React.useState<'market' | 'tenant' | undefined>();
  const [selectedRow, setSelectedRow] = React.useState<string | null>(null);
  const [tenants, setTenants] = React.useState<ITenant[]>([]);
  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    configuratorType: 'DCC' | 'SCC';
    row: ITenantConfig | null;
  }>({
    isOpen: false,
    configuratorType: 'DCC',
    row: null,
  });
  const [dialogRowForValidation, setDialogRowForValidation] = React.useState<ITenantConfig | null>(null);

  if (!user.groups.includes('3DEXHIBITION_SELFSERVICE_ADMIN')) {
    throw new CustomerFacingError(EErrorCode.UNAUTHORIZED);
  }

  const [tenantToEdit, setTenantToEdit] = React.useState<ITenantConfig | null>(null);

  React.useEffect(() => {
    sendApiRequest('/tenants').then((data) => {
      setTenants(data as ITenant[]);
    });

    window.onkeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setTenantToEdit(null);
        setDialogRowForValidation(null);
      }
    };
  }, []);

  React.useEffect(() => {
    const hash = window.location.hash;
    const id = hash ? hash.substring(1) : null;
    if (id) {
      const selector = `td#row-${normalizeId(id)}`;
      const element = elementRef?.current?.querySelector(selector);
      setSelectedRow(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [rows, elementRef]);

  React.useEffect(() => {
    // Fetch data from the API
    sendApiRequest('/config?brand=*&market=*').then((data) => {
      (data as ITenantConfig[]).sort((a, b) => a.id.localeCompare(b.id));
      setRows(data as ITenantConfig[]);
    });
  }, []);

  const clearRowSelection = () => {
    setSelectedRow(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const handleDialogClose = () => {
    setDialogState({
      isOpen: false,
      configuratorType: 'DCC',
      row: null,
    });
  };

  const handleGoToConfig = () => {
    if (dialogState.row) {
      setDialogRowForValidation(dialogState.row); // Store row for validation
      setTenantToEdit(dialogState.row);
    }
  };

  const handleConfiguratorValidation = (
    configuratorType: 'DCC' | 'SCC',
    hasLinks: boolean,
    updatedConfig?: ITenantConfig
  ) => {
    // Only validate if we came from the dialog (user was trying to enable feature without links)
    if (dialogRowForValidation && updatedConfig) {
      const field = configuratorType === 'DCC' ? CONFIGURATOR_FIELDS.DCC : CONFIGURATOR_FIELDS.SCC;

      if (hasLinks) {
        // Auto-check the checkbox if links are configured after save
        const newFlags = {
          ...updatedConfig.flags,
          [field]: true,
        };
        saveConfig(updatedConfig, newFlags);
      } else {
        // Auto-uncheck the checkbox if no links are configured after save
        const newFlags = {
          ...updatedConfig.flags,
          [field]: false,
        };
        saveConfig(updatedConfig, newFlags);
      }
    }
  };

  const handleCheckboxChange = (row: ITenantConfig, field: keyof ITenantConfig['flags'], value?: boolean) => {
    if (!row) return;

    const newValue = value ?? !row.flags[field];

    // Check if this is a DCC or SCC checkbox being enabled
    if (newValue && (field === CONFIGURATOR_FIELDS.DCC || field === CONFIGURATOR_FIELDS.SCC)) {
      const configuratorType = field === CONFIGURATOR_FIELDS.DCC ? 'DCC' : 'SCC';
      const configuratorKey = field === CONFIGURATOR_FIELDS.DCC ? 'dcc' : 'scc';

      // Check if there are any configured links for this configurator type
      const hasLinks = row[configuratorKey] && row[configuratorKey].length > 0;

      if (!hasLinks) {
        // Show dialog to guide user to add links, but still enable the checkbox
        setDialogState({
          isOpen: true,
          configuratorType,
          row,
        });
        // Enable the checkbox even without links
        const newFlags = {
          ...row.flags,
          [field]: newValue,
        };
        saveConfig(row, newFlags);
        return;
      }
    }

    const newFlags = {
      ...row.flags,
      [field]: newValue,
    };

    saveConfig(row, newFlags);

    if (row.id !== selectedRow) {
      clearRowSelection();
    }
  };

  const saveConfig = async (row: ITenantConfig, flags?: IFeatureFlags) => {
    try {
      // Optimistic update
      setRows(rows.map((r) => (r.id === row.id ? { ...row, flags: flags || row.flags } : r)));

      // API call
      await sendApiRequest(`/config/${row.id}`, {
        method: 'PUT',
        body: { ...row, flags: flags || row.flags },
      });
    } catch (error) {
      // Revert on error
      setRows(rows);
      console.error('Failed to update:', error);
    }
  };

  // Enable or disable market
  const toggleMarket = async (row: ITenantConfig) => {
    try {
      if (!row) return;

      const newRow = { ...row, enabled: row.enabled === undefined ? false : !row.enabled };

      // Optimistic update
      setRows(rows.map((r) => (r.id === row.id ? { ...newRow } : r)));

      // API call
      await sendApiRequest(`/config/${row.id}`, {
        method: 'PUT',
        body: { ...newRow },
      });
    } catch (error) {
      // Revert on error
      setRows(rows);
      console.error('Failed to update:', error);
    }

    if (row.id !== selectedRow) {
      clearRowSelection();
    }
  };

  /**
   * Deleting should never happen, but in case it's needed, users should add ?donald=true to the URL
   */
  const deleteItem = async (row: ITenantConfig) => {
    if (import.meta.env.MODE !== 'development') {
      return;
    }

    if (!getCurrentUrlParameter('donald')) {
      window.alert(`You don't have permission to delete this item.`);
      return;
    }

    const isConfirmed = window.confirm(`Are you sure you want to delete the configuration for ${row.id}?`);
    if (!isConfirmed) return;

    try {
      await sendApiRequest(`/config/${row.id}`, {
        method: 'DELETE',
      });
      setRows(rows.filter((r) => r.id !== row.id));
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
    clearRowSelection();
  };

  return (
    <div className="min-h-full bg-gray-10050" ref={elementRef}>
      <div className="py-10">
        <header>
          <div className="mx-auto max-w-7xl px-1 sm:flex sm:items-center sm:justify-between sm:px-2 lg:px-4">
            <h1 className="mb-5 text-3xl font-bold tracking-tight text-gray-900">3D Exhibition Self-Service</h1>
            <div className="mt-3 flex space-x-2 sm:ml-4 sm:mt-0">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-teal-dark px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
                onClick={() => setForm('market')}
              >
                New Market
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-teal-dark px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
                onClick={() => setForm('tenant')}
              >
                New Tenant
              </button>
            </div>
          </div>
        </header>
        {form === 'market' && <NewMarketForm onCancel={() => setForm(undefined)} />}
        {form === 'tenant' && (
          <NewTenantForm
            onCancel={() => setForm(undefined)}
            onSave={(tenant: ITenant) => {
              setForm('market');
              setTenants([...tenants, tenant]);
            }}
          />
        )}
        <main className="">
          <div className="mx-auto max-w-7xl bg-white shadow sm:rounded-lg ">
            {/* Your content */}
            <div className="flow-root">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      {Array.from(columns).map(([key, column]) => (
                        <th
                          key={key}
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900"
                        >
                          {column}
                        </th>
                      ))}
                      {import.meta.env.MODE === 'development' && (
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">
                          Delete
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-300 bg-white">
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={classNames(
                          selectedRow === row.id ? 'bg-orange-100 hover:bg-green-100' : 'hover:bg-gray-10050'
                        )}
                      >
                        <td
                          className="max-w-48 truncate py-4 pl-4 pr-3 text-xs font-bold text-gray-900"
                          id={`row-${normalizeId(row.id)}`}
                          title={row.id}
                        >
                          <span
                            role="button"
                            onClick={() => {
                              setDialogRowForValidation(null);
                              setTenantToEdit(row);
                            }}
                          >
                            {row.id}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs font-medium text-gray-900">
                          {brandNames[row.brand as keyof typeof brandNames] ?? row.brand}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-700">{row.market}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-700">
                          {tenants.find((tenant) => tenant.id === row.tenant)?.name ?? 'All tenants'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-center text-xs ">
                          <input
                            type="checkbox"
                            className="size-4 rounded border-gray-300 text-teal focus:ring-teal"
                            checked={row.enabled !== false}
                            onChange={() => toggleMarket(row)}
                          />
                        </td>
                        {editableFields.map((field) => (
                          <td key={field} className="whitespace-nowrap px-3 py-4 text-center text-xs ">
                            <input
                              type="checkbox"
                              className="size-4 rounded border-gray-300 text-teal focus:ring-teal"
                              // @ts-expect-error it's ok
                              checked={!!row.flags[field]}
                              // @ts-expect-error it's ok
                              onChange={() => handleCheckboxChange(row, field)}
                            />
                          </td>
                        ))}
                        {import.meta.env.MODE === 'development' && (
                          <td className="whitespace-nowrap px-3 py-4 text-center text-xs text-gray-700">
                            <button
                              type="button"
                              className="rounded-full bg-red-600 p-0.5 text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-900 active:bg-red-900"
                              onClick={() => deleteItem(row)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="24">
                                <path
                                  fill="white"
                                  d="M20 18h2v16h-2zm4 0h2v16h-2zm4 0h2v16h-2zm-16-6h26v2H12zm18 0h-2v-1c0-.6-.4-1-1-1h-4c-.6 0-1 .4-1 1v1h-2v-1c0-1.7 1.3-3 3-3h4c1.7 0 3 1.3 3 3v1z"
                                />
                                <path
                                  fill="white"
                                  d="M31 40H19c-1.6 0-3-1.3-3.2-2.9l-1.8-24 2-.2 1.8 24c0 .6.6 1.1 1.2 1.1h12c.6 0 1.1-.5 1.2-1.1l1.8-24 2 .2-1.8 24C34 38.7 32.6 40 31 40z"
                                />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Layout */}
          </div>
        </main>
      </div>
      {tenantToEdit && (
        <MarketDashboard
          tenant={tenantToEdit}
          onDone={() => {
            setTenantToEdit(null);
            setDialogRowForValidation(null);
          }}
          tenantName={tenants.find((tenant) => tenant.id === tenantToEdit.tenant)?.name ?? ''}
          onTenantUpdate={(updatedTenant) => {
            // Update the rows state to reflect the changes
            setRows(rows.map((r) => (r.id === updatedTenant.id ? updatedTenant : r)));
            // Update tenantToEdit so the dialog has the latest data
            setTenantToEdit(updatedTenant);
          }}
          onConfiguratorValidation={handleConfiguratorValidation}
        />
      )}
      <ConfiguratorValidationDialog
        isOpen={dialogState.isOpen}
        onClose={handleDialogClose}
        onGoToConfig={handleGoToConfig}
        configuratorType={dialogState.configuratorType}
      />
    </div>
  );
};
